/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchLocations } from "../../api";
import Select, { optionCSS, valueContainerCSS, controlCSS, menuCSS } from "../stop-finder/Select";
import { components } from "react-select";
import { useBreakpointsContext } from "../../hooks";

const searchValueContainerCSS = css`
    padding-left: 12px;
`;
const searchMenuCSS = ({ selectProps }) => css`
    ${menuCSS}

    border-radius: ${selectProps.menuPlacement === "top" ? "4px 4px 0 0" : "0 0 4px 4px"};
    overflow: hidden;
`;
const searchControlCSS = ({ menuIsOpen, selectProps }) => css`
    padding-left: 16px;
    border-radius: ${menuIsOpen ? (selectProps.menuPlacement === "top" ? "0 0 4px 4px" : "4px 4px 0 0") : "4px"};
    border: none !important;
    box-shadow: 0px 0px 30px 0px rgba(23, 21, 84, 0.15) !important;
`;
const searchOptionCSS = css`
    ${optionCSS}

    label: searchOptionCSS;

    height: auto;
    min-height: 48px;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 8px 16px;
`;
const Option = props => {
    const { data, innerRef, innerProps } = props;
    return (
        <div ref={innerRef} css={searchOptionCSS} {...innerProps}>
            <div>
                <strong>{data.name}</strong>
            </div>
            <span>{data.desc}</span>
        </div>
    );
};
const Control = ({ children, ...props }) => {
    return (
        <components.Control {...props}>
            <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M14 7.21502C14 10.9313 11.0749 14.2387 7.39055 17.5988C7.16956 17.8004 6.83044 17.8004 6.60945 17.5988C2.92513 14.2387 0 10.9313 0 7.21502C0 3.36835 3.13401 0.25 7 0.25C10.866 0.25 14 3.36835 14 7.21502Z"
                    fill="#504CF3"
                />
                <path
                    d="M9.33317 7.21511C9.33317 8.49733 8.2885 9.53678 6.99984 9.53678C5.71117 9.53678 4.6665 8.49733 4.6665 7.21511C4.6665 5.93288 5.71117 4.89343 6.99984 4.89343C8.2885 4.89343 9.33317 5.93288 9.33317 7.21511Z"
                    fill="#D3D2FC"
                />
            </svg>
            {children}
        </components.Control>
    );
};
const selectStyles = {
    valueContainer: (provided, state) => [provided, valueContainerCSS, searchValueContainerCSS],
    control: (provided, state) => [provided, controlCSS(state), searchControlCSS(state)],
    menu: (provided, state) => [provided, menuCSS, searchMenuCSS(state)],
    menuPortal: base => ({ ...base, zIndex: 3 }),
};

function Search(props) {
    const { setStartingPoint, className } = props;
    const [value, setValue] = useState();
    const { isDesktop } = useBreakpointsContext();

    const loadOptions = useCallback(async query => {
        if (!query) {
            return [];
        }
        const locations = await fetchLocations(query);
        return locations;
    }, []);
    const getOptionLabel = useCallback(option => `${option.name}${option.desc ? `, ${option.desc}` : ""}`, []);
    const loadingMessage = useCallback(() => "Searching...", []);
    const getOptionValue = useCallback(option => option.id, []);
    const components = useMemo(() => {
        return { Option, Control };
    }, []);

    useEffect(() => {
        setStartingPoint(value);
    }, [value, setStartingPoint]);

    return (
        <Select
            styles={selectStyles}
            className={className}
            placeholder="Address, City, Postal Code"
            cacheOptions
            async
            loadOptions={loadOptions}
            value={value}
            onChange={setValue}
            getOptionLabel={getOptionLabel}
            loadingMessage={loadingMessage}
            hideIndicator={true}
            getOptionValue={getOptionValue}
            components={components}
            menuShouldScrollIntoView={false}
            menuPlacement={isDesktop ? "top" : "auto"}
        />
    );
}

export default Search;
